<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import {
  AlertTriangle,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardCheck,
  RefreshCw,
  Terminal,
  Trash2,
  X,
} from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import LogFilterBar from '@/components/LogFilterBar.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'
import { useDialog } from '@/composables/useDialog'
import AppLayout from '@/layouts/AppLayout.vue'
import { highlightJson } from '@/lib/syntax-highlight'
import { formatDateTime } from '@/lib/utils'
import type { BreadcrumbItem } from '@/types'

const devLogsPath = () => '/dev_logs'
const clearDevLogsPath = () => '/dev_logs/clear'

interface DevLogEntry {
  id: number
  timestamp: string
  level: string
  pid: number
  thread: string
  duration_ms: number | null
  name: string
  message: string
  payload: Record<string, unknown>
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  level?: string | null
  name?: string | null
  date_from?: string | null
  date_to?: string | null
}

const props = defineProps<{
  entries: DevLogEntry[]
  names: string[]
  levels: string[]
  pagination: PaginationMeta
  filters: Filters
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Dev Logs', href: devLogsPath() }]

const ALL = '__all__'

const search = ref(props.filters.q ?? '')
const levelFilter = ref(props.filters.level ?? ALL)
const nameFilter = ref(props.filters.name ?? ALL)
const dateFrom = ref(props.filters.date_from ?? '')
const dateTo = ref(props.filters.date_to ?? '')
const expandedId = ref<number | null>(null)
const autoRefresh = ref(false)
const { copy, isCopied } = useCopyToClipboard()

// Live-mode state — decoupled from raw props so we control when new entries appear
const displayedEntries = ref<DevLogEntry[]>([...props.entries])
const pendingEntries = ref<DevLogEntry[]>([])
const highlightedTs = ref(new Set<string>())
const isPollingReload = ref(false)

// Sync displayed entries with prop changes
watch(
  () => props.entries,
  (fresh) => {
    if (!isPollingReload.value) {
      // Normal navigation (filter change, page turn, etc.)
      displayedEntries.value = [...fresh]
      pendingEntries.value = []
      return
    }

    isPollingReload.value = false

    // Auto-refresh: find entries newer than the top of the current view
    const cutoff = displayedEntries.value[0]?.timestamp ?? ''
    if (!cutoff) {
      displayedEntries.value = [...fresh]
      return
    }

    const newer = fresh.filter((e) => e.timestamp > cutoff)
    if (newer.length > 0) {
      pendingEntries.value = newer
    }
  },
)

const hasActiveFilters = computed(
  () =>
    search.value !== '' ||
    levelFilter.value !== ALL ||
    nameFilter.value !== ALL ||
    dateFrom.value !== '' ||
    dateTo.value !== '',
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  level: levelFilter.value !== ALL ? levelFilter.value : undefined,
  name: nameFilter.value !== ALL ? nameFilter.value : undefined,
  date_from: dateFrom.value || undefined,
  date_to: dateTo.value || undefined,
}))

function applyFilters() {
  pendingEntries.value = []
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(devLogsPath(), params, { preserveState: true, replace: true })
}

let searchTimer: ReturnType<typeof setTimeout>
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})
watch([levelFilter, nameFilter, dateFrom, dateTo], applyFilters)

function clearFilters() {
  search.value = ''
  levelFilter.value = ALL
  nameFilter.value = ALL
  dateFrom.value = ''
  dateTo.value = ''
}

// Auto-refresh
let refreshInterval: ReturnType<typeof setInterval> | null = null
let highlightTimer: ReturnType<typeof setTimeout> | null = null

watch(autoRefresh, (on) => {
  if (on) {
    refreshInterval = setInterval(() => {
      isPollingReload.value = true
      router.reload({ only: ['entries', 'pagination'] })
    }, 5000)
  } else {
    if (refreshInterval) clearInterval(refreshInterval)
    pendingEntries.value = []
  }
})

onUnmounted(() => {
  clearTimeout(searchTimer)
  if (refreshInterval) clearInterval(refreshInterval)
  if (highlightTimer) clearTimeout(highlightTimer)
})

function loadPending() {
  highlightedTs.value = new Set(pendingEntries.value.map((e) => e.timestamp))
  displayedEntries.value = [...pendingEntries.value, ...displayedEntries.value]
  pendingEntries.value = []

  if (highlightTimer) clearTimeout(highlightTimer)
  highlightTimer = setTimeout(() => {
    highlightedTs.value = new Set()
  }, 15_000)
}

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id
}

async function copyPayload(entry: DevLogEntry) {
  await copy(JSON.stringify(entry.payload, null, 2), entry.id)
}


const LEVEL_CLASS: Record<string, string> = {
  debug: 'border-transparent bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  info: 'border-transparent bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  warn: 'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  error: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  fatal: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
}

function levelClass(level: string) {
  return LEVEL_CLASS[level?.toLowerCase()] ?? LEVEL_CLASS.debug
}

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—'
  return ms < 1 ? `${(ms * 1000).toFixed(0)}µs` : `${ms.toFixed(1)}ms`
}

// ── Error payload helpers ────────────────────────────────────────────────────

interface ExceptionInfo {
  name: string
  message: string
  backtrace: string[]
  cause?: ExceptionInfo
}

function parseBacktrace(raw: unknown): string[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean)
  if (typeof raw === 'string')
    return raw
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
  return []
}

function extractException(payload: Record<string, unknown>): ExceptionInfo | null {
  // Format 1: nested `exception` object (standard semantic_logger JSON)
  const exc = payload?.exception
  if (exc && typeof exc === 'object') {
    const e = exc as Record<string, unknown>
    return {
      name: String(e.name ?? e.class_name ?? e.class ?? 'Unknown'),
      message: String(e.message ?? ''),
      backtrace: parseBacktrace(e.backtrace ?? e.stack_trace ?? e.stack),
      cause: e.cause ? (extractException({ exception: e.cause }) ?? undefined) : undefined,
    }
  }

  // Format 2: flat keys at payload root (some appenders / custom loggers)
  if (payload?.exception_class ?? payload?.exception_name) {
    return {
      name: String(payload.exception_class ?? payload.exception_name ?? 'Unknown'),
      message: String(payload.exception_message ?? ''),
      backtrace: parseBacktrace(payload.backtrace ?? payload.stack_trace ?? payload.stack),
    }
  }

  return null
}

function payloadWithoutException(payload: Record<string, unknown>): Record<string, unknown> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { exception: _exc, ...rest } = payload
  return rest
}

function isAppLine(line: string): boolean {
  return /^\s*(app\/|\/.*\/app\/)/.test(line)
}

const tableColumns: TableColumn[] = [
  { key: 'expand', label: '', headerClass: 'w-8' },
  { key: 'time', label: 'Time', headerClass: 'w-40' },
  { key: 'level', label: 'Level', headerClass: 'w-20' },
  { key: 'logger', label: 'Logger', headerClass: 'w-52' },
  { key: 'message', label: 'Message' },
  { key: 'duration', label: 'Duration', align: 'right', headerClass: 'w-24' },
]

const BACKTRACE_PREVIEW = 12
const expandedBacktraces = ref(new Set<number>())

// ── Clear logs ───────────────────────────────────────────────────────────────
const { confirm } = useDialog()

async function doClears() {
  const ok = await confirm({
    title: 'Clear dev logs?',
    description: 'This will permanently delete all but the last 50 log entries. This cannot be undone.',
    confirmLabel: 'Clear',
  })
  if (!ok) return
  router.delete(clearDevLogsPath(), { preserveState: false })
}

function toggleBacktrace(id: number) {
  const s = new Set(expandedBacktraces.value)
  if (s.has(id)) {
    s.delete(id)
  } else {
    s.add(id)
  }
  expandedBacktraces.value = s
}
</script>

<template>
  <Head title="Dev Logs" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 p-4">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Terminal class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Dev Logs</h1>
            <p class="text-muted-foreground text-sm">Last 2 000 log entries from development.json.log</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button variant="outline" size="sm" class="gap-1.5 text-destructive hover:text-destructive" @click="doClears()">
            <Trash2 class="size-3.5" />
            Clear
          </Button>
          <Button
            :variant="autoRefresh ? 'default' : 'outline'"
            size="sm"
            class="gap-1.5"
            @click="autoRefresh = !autoRefresh"
          >
            <RefreshCw class="size-3.5" :class="autoRefresh ? 'animate-spin' : ''" />
            {{ autoRefresh ? 'Live' : 'Auto-refresh' }}
          </Button>
        </div>
      </div>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">Log Entries</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} entr{{ pagination.total_count !== 1 ? 'ies' : 'y' }}
                {{ hasActiveFilters ? 'found' : 'total' }}
              </CardDescription>
            </div>
            <Terminal class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Filters -->
        <LogFilterBar
          v-model:search="search"
          v-model:date-from="dateFrom"
          v-model:date-to="dateTo"
          :has-active-filters="hasActiveFilters"
          search-placeholder="Search message or logger…"
          @clear="clearFilters"
        >
          <Select v-model="levelFilter">
            <SelectTrigger class="w-full sm:w-32">
              <SelectValue placeholder="Any level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any level</SelectItem>
              <SelectItem v-for="l in levels" :key="l" :value="l">
                {{ l.charAt(0).toUpperCase() + l.slice(1) }}
              </SelectItem>
            </SelectContent>
          </Select>

          <Select v-model="nameFilter">
            <SelectTrigger class="w-full sm:w-52">
              <SelectValue placeholder="Any logger" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any logger</SelectItem>
              <SelectItem v-for="n in names" :key="n" :value="n">{{ n }}</SelectItem>
            </SelectContent>
          </Select>
        </LogFilterBar>

        <CardContent class="relative p-0">
          <!-- ── New-entries notification pill ── -->
          <Transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="opacity-0 -translate-y-2 scale-95"
            enter-to-class="opacity-100 translate-y-0 scale-100"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0 scale-100"
            leave-to-class="opacity-0 -translate-y-2 scale-95"
          >
            <div v-if="pendingEntries.length > 0" class="pointer-events-none sticky top-2 z-10 flex justify-center">
              <button
                class="pointer-events-auto flex items-center gap-2 rounded-full border border-indigo-400/50 bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white shadow-lg shadow-indigo-500/25 transition-all hover:scale-105 hover:bg-indigo-700 active:scale-100 dark:border-indigo-400/30 dark:bg-indigo-600/90"
                @click="loadPending"
              >
                <!-- Pulsing dot -->
                <span class="relative flex size-2 shrink-0">
                  <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-200 opacity-75" />
                  <span class="relative inline-flex size-2 rounded-full bg-white" />
                </span>
                <ArrowUp class="size-3" />
                {{ pendingEntries.length }} new {{ pendingEntries.length === 1 ? 'entry' : 'entries' }}
              </button>
            </div>
          </Transition>

          <!-- Empty state -->
          <div v-if="displayedEntries.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <Terminal class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{
                hasActiveFilters
                  ? 'No entries match your filters'
                  : 'No log entries yet — make a request to populate the log'
              }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable v-else :columns="tableColumns" compact table-fixed>
            <template v-for="entry in displayedEntries" :key="entry.timestamp + entry.id">
              <!-- Summary row -->
              <tr
                class="border-border cursor-pointer border-b select-none"
                :class="[
                  expandedId === entry.id ? 'bg-muted/60' : 'hover:bg-muted/40',
                  highlightedTs.has(entry.timestamp) ? 'row-highlight' : 'transition-colors',
                ]"
                @click="toggleExpand(entry.id)"
              >
                <td class="px-3 py-3 text-center">
                  <ChevronDown v-if="expandedId === entry.id" class="text-muted-foreground size-4" />
                  <ChevronRight v-else class="text-muted-foreground size-4" />
                </td>
                <td class="text-muted-foreground px-4 py-3 text-xs whitespace-nowrap">
                  {{ formatDateTime(entry.timestamp) }}
                </td>
                <td class="px-4 py-3">
                  <Badge variant="outline" :class="levelClass(entry.level)" class="text-[10px] uppercase">
                    {{ entry.level }}
                  </Badge>
                </td>
                <td class="px-4 py-3 font-medium">
                  <span class="block truncate text-xs">{{ entry.name }}</span>
                </td>
                <td class="px-4 py-3">
                  <span class="line-clamp-1 text-xs">{{ entry.message }}</span>
                </td>
                <td class="text-muted-foreground px-4 py-3 text-right font-mono text-xs whitespace-nowrap">
                  {{ formatDuration(entry.duration_ms) }}
                </td>
              </tr>

              <!-- Expanded detail row -->
              <tr v-if="expandedId === entry.id" class="border-border border-b">
                <td colspan="6" class="bg-muted/30 px-6 py-5">
                  <div class="flex items-start gap-1">
                    <div class="min-w-0 flex-1 space-y-3">
                      <!-- Meta grid -->
                      <div class="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-4">
                        <div>
                          <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Level</p>
                          <Badge
                            variant="outline"
                            :class="levelClass(entry.level)"
                            class="mt-0.5 text-[10px] uppercase"
                          >
                            {{ entry.level }}
                          </Badge>
                        </div>
                        <div>
                          <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Logger</p>
                          <p class="mt-0.5 text-xs font-medium">{{ entry.name }}</p>
                        </div>
                        <div>
                          <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                            PID / Thread
                          </p>
                          <p class="mt-0.5 font-mono text-xs">{{ entry.pid }} · {{ entry.thread }}</p>
                        </div>
                        <div>
                          <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Duration</p>
                          <p class="mt-0.5 font-mono text-xs">{{ formatDuration(entry.duration_ms) }}</p>
                        </div>
                      </div>

                      <!-- ── Error payload: rich exception view ── -->
                      <template
                        v-if="
                          ['error', 'fatal'].includes(entry.level?.toLowerCase()) && extractException(entry.payload)
                        "
                      >
                        <div class="space-y-3">
                          <!-- Exception header -->
                          <div
                            class="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800/40 dark:bg-red-950/30"
                          >
                            <div class="flex items-start gap-2">
                              <AlertTriangle class="mt-0.5 size-4 shrink-0 text-red-500 dark:text-red-400" />
                              <div class="min-w-0 space-y-0.5">
                                <p class="font-mono text-xs font-semibold text-red-700 dark:text-red-300">
                                  {{ extractException(entry.payload)!.name }}
                                </p>
                                <p class="text-xs text-red-600 dark:text-red-400">
                                  {{ extractException(entry.payload)!.message }}
                                </p>
                              </div>
                            </div>
                          </div>

                          <!-- Cause (chained exception) -->
                          <div
                            v-if="extractException(entry.payload)!.cause"
                            class="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 dark:border-orange-800/40 dark:bg-orange-950/20"
                          >
                            <p
                              class="mb-0.5 text-[10px] font-semibold tracking-wide text-orange-500 uppercase dark:text-orange-400"
                            >
                              Caused by
                            </p>
                            <p class="font-mono text-xs font-semibold text-orange-700 dark:text-orange-300">
                              {{ extractException(entry.payload)!.cause!.name }}
                            </p>
                            <p class="text-xs text-orange-600 dark:text-orange-400">
                              {{ extractException(entry.payload)!.cause!.message }}
                            </p>
                          </div>

                          <!-- Backtrace -->
                          <div
                            v-if="extractException(entry.payload)!.backtrace.length > 0 || entry.payload.exception"
                          >
                            <p class="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                              Backtrace
                            </p>
                            <div
                              class="bg-background border-border overflow-hidden rounded-md border font-mono text-xs"
                            >
                              <div
                                v-for="(line, i) in expandedBacktraces.has(entry.id)
                                  ? extractException(entry.payload)!.backtrace
                                  : extractException(entry.payload)!.backtrace.slice(0, BACKTRACE_PREVIEW)"
                                :key="i"
                                class="flex items-baseline gap-3 border-b px-3 py-1 last:border-0"
                                :class="
                                  isAppLine(line)
                                    ? 'text-foreground border-amber-100 bg-amber-50/60 dark:border-amber-900/20 dark:bg-amber-950/20'
                                    : 'border-border/40 text-muted-foreground'
                                "
                              >
                                <span class="w-5 shrink-0 text-right text-[10px] opacity-30 select-none">{{
                                  i + 1
                                }}</span>
                                <span class="break-all" :class="isAppLine(line) ? 'font-medium' : ''">{{
                                  line
                                }}</span>
                              </div>

                              <!-- No backtrace available — raw exception dump for debugging -->
                              <div
                                v-if="extractException(entry.payload)!.backtrace.length === 0"
                                class="border-border/40 text-muted-foreground px-3 py-2 text-[10px] italic"
                              >
                                No backtrace captured.
                                <span class="font-mono">exception</span> raw:
                                {{ JSON.stringify(entry.payload.exception, null, 2) }}
                              </div>

                              <!-- Show more / less -->
                              <button
                                v-if="extractException(entry.payload)!.backtrace.length > BACKTRACE_PREVIEW"
                                class="text-muted-foreground hover:text-foreground hover:bg-muted/40 w-full border-t px-3 py-1.5 text-left text-[10px] transition-colors"
                                @click.stop="toggleBacktrace(entry.id)"
                              >
                                {{
                                  expandedBacktraces.has(entry.id)
                                    ? '↑ Show less'
                                    : `↓ ${extractException(entry.payload)!.backtrace.length - BACKTRACE_PREVIEW} more lines`
                                }}
                              </button>
                            </div>
                          </div>

                          <!-- Remaining context (everything except exception key) -->
                          <div v-if="Object.keys(payloadWithoutException(entry.payload)).length > 0">
                            <p class="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                              Context
                            </p>
                            <pre
                              class="bg-background border-border max-h-48 overflow-x-auto overflow-y-auto rounded-md border p-3 font-mono text-xs leading-relaxed"
                              v-html="highlightJson(payloadWithoutException(entry.payload))"></pre
                            >
                          </div>
                        </div>
                      </template>

                      <!-- ── Default payload: raw JSON ── -->
                      <template v-else>
                        <div v-if="Object.keys(entry.payload).length > 0">
                          <p class="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                            Payload
                          </p>
                          <pre
                            class="bg-background border-border max-h-96 overflow-x-auto overflow-y-auto rounded-md border p-3 font-mono text-xs leading-relaxed"
                            v-html="highlightJson(entry.payload)"></pre
                          >
                        </div>
                        <p v-else class="text-muted-foreground text-xs italic">No additional payload</p>
                      </template>
                    </div>

                    <!-- Copy button -->
                    <Button
                      v-if="Object.keys(entry.payload).length > 0"
                      variant="ghost"
                      size="sm"
                      class="text-muted-foreground hover:text-foreground mt-0.5 h-6 shrink-0 gap-1.5 px-2 text-xs"
                      @click.stop="copyPayload(entry)"
                    >
                      <ClipboardCheck v-if="isCopied(entry.id)" class="size-3.5 text-green-600" />
                      <Clipboard v-else class="size-3.5" />
                      {{ isCopied(entry.id) ? 'Copied' : 'Copy' }}
                    </Button>
                  </div>
                </td>
              </tr>
            </template>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="devLogsPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>

<style scoped>
/* Row fades from indigo tint → transparent over 10 seconds */
@keyframes row-highlight {
  0% {
    background-color: rgb(224 231 255 / 0.65);
  }
  20% {
    background-color: rgb(224 231 255 / 0.45);
  }
  100% {
    background-color: transparent;
  }
}

@keyframes row-highlight-dark {
  0% {
    background-color: rgb(49 46 129 / 0.3);
  }
  20% {
    background-color: rgb(49 46 129 / 0.2);
  }
  100% {
    background-color: transparent;
  }
}

.row-highlight {
  animation: row-highlight 10s ease-out forwards;
}

:global(.dark) .row-highlight {
  animation: row-highlight-dark 10s ease-out forwards;
}
</style>
